import { useCallback, useMemo } from "react";

import { APIError } from "shared/constants/errorTypes";
import { cleanObject } from "shared/helper-functions/objects/objectHelpers";
import { getEndpoints } from "app/auth/authService";
import { useApiService } from "./useApiService";

export const useApi = () => {
    const { callBackend } = useApiService();

    const buildResource = (baseResource, serviceOptions) => {
        let resource = baseResource;
        if (serviceOptions?.urlParams) {
            serviceOptions.urlParams.forEach(param => {
                resource = resource.replace(param.name, param.value);
            });
        }
        return resource;
    };

    const buildOptions = (serviceOptions, defaultMethod) => ({
        method: serviceOptions?.method || defaultMethod,
        params: (serviceOptions && cleanObject(serviceOptions.params)) || undefined,
        body: serviceOptions?.body || undefined,
    });

    // Parses error response body safely
    const parseErrorBody = async (response) => {
        try {
            return await response.json();
        } catch (e) {
            console.error("Failed to parse error response body", e);
            return null;
        }
    };

    /**
     * Creates APIError from different bestwork backend error response formats
     * Handles 3 error formats:
     * 1. Exception throw in backend is captured by GlobalExceptionHandlerMiddleware. Body fields: ex, message, status, error, timestamp.
     * 2. Custom ErrorResult in backend. Not capture by GlobalExceptionHandlerMiddleware. Don't use this anymore
     * 3. Return helper method from backend like "BadRequest".. Not capture by GlobalExceptionHandlerMiddleware. Don't use this anymore.
     */
    const createError = useCallback(async (response) => {
        const correlationId = response?.headers?.has("X-Correlation-ID")
            ? response.headers.get("X-Correlation-ID")
            : null;

        const errorBody = await parseErrorBody(response);

        let message, status, rawError, detail;

        // Case 1: GlobalExceptionHandlerMiddleware - has 'ex' field
        if (errorBody?.ex) {
            message = errorBody.message || "Server error occurred";
            status = errorBody.status || response.status;
            rawError = errorBody.ex;
            detail = errorBody.errorDetails || errorBody.message;
        }
        // Case 2: Legacy ErrorResult - has 'title' field
        else if (errorBody?.title) {
            message = errorBody.detail || errorBody.title || "Server error occurred";
            status = response.status;
            rawError = response.url;
            detail = errorBody.detail;
        }
        // Case 3: Simple text response or unknown format
        else {
            message = typeof errorBody === "string" ? errorBody : "Server error occurred";
            status = response.status;
            rawError = response.url;
            detail = message;
        }

        return new APIError(message, status, correlationId, detail, rawError);
    }, []);

    const validateResponse = useCallback(async (response) => {
        // Handle undefined/null responses. We should check this.
        if (!response) {
            return null;
        }

        // Success case - return response as-is
        if (response.ok) {
            return response;
        }

        // When we throw the error here, it will be caught in AppErrorBoundary, that is a global wrapper in the App.js.
        // AppErrorBoundary takes unahndle errors and use the { handleError } to show the error modal.
        // This allows centralized error handling for unexpected errors all over the app.
        // If we want to handle specific errors in specific calls, we can just add try and catch in those calls.
        const serverError = await createError(response);
        console.error("API Error in useApi: ", serverError);
        throw serverError;

    }, [createError]);

    const makeRequest = useCallback(async (serviceName, serviceOptions, defaultMethod) => {
        const config = await getEndpoints(serviceName);
        const resource = buildResource(config.resource, serviceOptions);
        const options = buildOptions(serviceOptions, defaultMethod);
        const response = await callBackend(config.api, config.version, resource, options);

        const result = await validateResponse(response);
        return result;

    }, [callBackend, validateResponse]);

    const getDataAsync = useCallback((serviceName, serviceOptions) =>
        makeRequest(serviceName, serviceOptions, "GET"), [makeRequest]);

    const postData = useCallback((serviceName, serviceOptions) =>
        makeRequest(serviceName, serviceOptions, "POST"), [makeRequest]);

    const putData = useCallback((serviceName, serviceOptions) =>
        makeRequest(serviceName, serviceOptions, "PUT"), [makeRequest]);

    const patchData = useCallback((serviceName, serviceOptions) =>
        makeRequest(serviceName, serviceOptions, "PATCH"), [makeRequest]);

    const deleteData = useCallback((serviceName, serviceOptions) =>
        makeRequest(serviceName, serviceOptions, "DELETE"), [makeRequest]);

    return useMemo(
        () => ({
            getDataAsync,
            postData,
            putData,
            patchData,
            deleteData,
        }),
        [getDataAsync, postData, putData, patchData, deleteData]
    );
};
